import React from 'react';
import { useHistory } from 'react-router-dom';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTranslation } from 'react-i18next';
import { UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

type MutationOptions = UseMutationOptions<AxiosResponse<any>, unknown, any, unknown>;

type Args = {
    queryInvalidateFunction: () => void;
    // required for rule type
    ruleId?: string;
    mutationOptions: MutationOptions;
    mode: 'deleteTenantOverride' | 'updateTenantOverride';
};

function useTenantErrorHandler(
    { mutationOptions, queryInvalidateFunction, mode }: Args = {
        mutationOptions: {},
        queryInvalidateFunction: () => {},
        mode: 'deleteTenantOverride'
    }
) {
    const { t } = useTranslation();

    const history = useHistory();
    const { addTechnicalErrorModal } = useReduxModal();
    const { onError } = mutationOptions;

    const modalTitle = t('tenantOverrideNotSave');
    const isDeleteTenantOverride = mode === 'deleteTenantOverride';

    const errorHandler = React.useCallback(
        (error: any, variables?: any, context?: any) => {
            const handleNotFound = () => {
                if (isDeleteTenantOverride) {
                    queryInvalidateFunction();
                    addTechnicalErrorModal(
                        '',
                        t('Tenant Override Deleted'),
                        () => {
                            history.push('/global-rules');
                        },
                        {
                            status: 'warning',
                            content: t('alreadyDeletedTenantOverride')
                        }
                    );
                }
            };

            const handleDefault = () => {
                const preContentMessage = modalTitle;
                addTechnicalErrorModal(preContentMessage, preContentMessage);
            };

            switch (error?.status) {
                case 404:
                    handleNotFound();
                    break;

                default:
                    handleDefault();
                    break;
            }

            onError?.(error, variables, context);
        },
        [onError, isDeleteTenantOverride, queryInvalidateFunction, addTechnicalErrorModal, t, history, modalTitle]
    );

    return errorHandler;
}

export default useTenantErrorHandler;
